import { hydrateItem } from '@zealous/template/timeline/hydrateItem'
import type {
  TimelineAsset,
  TimelineDoc,
  TimelineItem,
  TranscriptParticipant,
} from '@zealous/template/timeline/types'
import { defaultClipScene } from '@zealous/template/types/defaultClipScene'
import { DEFAULT_WORDS } from '@zealous/template/types/defaultSubtitles'
import type { TimelineItemV3 } from '@zealous/template/types/renderConfigV2'
import { THEMES } from '@zealous/template/types/themes'
import { FPS } from '@zealous/template/types/video'

/**
 * Speaker pinning, proved in the Studio (#566): two hand-written
 * generation-3 documents in which each speaker element is pinned to ONE
 * participant of the scene's recording, and the active one is lit. Built
 * from persisted items through `hydrateItem`, as `hydrateTimeline` builds
 * them; `TimelineComposition` lowers the scene and its views at mount.
 *
 * Both carry a transcript asset whose spans alternate between `par_a` and
 * `par_b` with one deliberate gap longer than the hold, so scrubbing shows
 * the three states: A lit, B lit, both dim. Portrait only; square and
 * landscape are the blueprint's job in #596.
 */

const theme = THEMES[0]!.config
const WIDTH = 1080
const HEIGHT = 1920
const size = { width: WIDTH, height: HEIGHT }
const REC = 'rec_demo'
const SOURCE = { kind: 'recording', recordingId: REC } as const

/** The demo recording's two people: one from the demo scene, one invented with a 47-character name. */
const demoSpeaker = defaultClipScene.paragraphs[0]!.speaker
const participants: TranscriptParticipant[] = [
  {
    id: 'par_a',
    name: demoSpeaker.name,
    handle: demoSpeaker.username,
    avatarUrl: demoSpeaker.avatarUrl,
  },
  {
    id: 'par_b',
    name: 'Alexandria Montgomery-Fitzgerald of Northumbria',
    handle: '@alexandria',
    avatarUrl: 'https://picsum.photos/seed/pin-b/400/400',
  },
]

/** Alternating turns over a window, with a gap no span covers. */
function turns(
  spans: Array<[participant: 'par_a' | 'par_b', start: number, end: number]>,
) {
  return spans.map(([id, start, end]) => {
    const p = participants.find((x) => x.id === id)!
    return {
      start,
      end,
      name: p.name,
      handle: p.handle,
      avatarUrl: p.avatarUrl,
      participantId: id,
    }
  })
}

const textStyle = {
  fontFamily: theme.fonts.primary,
  fontWeight: 600,
  lineHeight: 1.2,
  letterSpacing: 0,
  color: '#ffffff',
  align: 'left',
  maxLines: 1,
} as const

const captionStyle = {
  color: '#ffffff',
  fontFamily: theme.fonts.primary,
  highlightColor: theme.colors.accent,
  highlightType: 'last-word',
  fontSize: 48,
  fontWeight: 700,
  linesPerPage: 2,
  lineHeight: 1.3,
  textAlign: 'center',
  scrollType: 'line-by-line',
} as const

function doc(
  tracks: Array<{ id: string; items: TimelineItemV3[] }>,
  assets: Record<string, TimelineAsset>,
  sceneAssetId: string,
): TimelineDoc {
  const items: Record<string, TimelineItem> = {}
  for (const track of tracks)
    for (const item of track.items)
      items[item.id] = hydrateItem(item, {
        ...size,
        assetId: item.type === 'scene' && item.source ? sceneAssetId : null,
      })
  return {
    fps: FPS,
    compositionWidth: WIDTH,
    compositionHeight: HEIGHT,
    theme,
    tracks: tracks.map((t) => ({
      id: t.id,
      items: t.items.map((i) => i.id),
      hidden: false,
      muted: false,
    })),
    items,
    assets,
    watermark: false,
  }
}

const nameChip = (
  id: string,
  participantId: 'par_a' | 'par_b',
  y: number,
  duration: number,
): TimelineItemV3 => ({
  id,
  type: 'text',
  from: 0,
  duration,
  text: '',
  style: {
    ...textStyle,
    fontSize: 36,
    // A 60 % black pill behind the name, for contrast over any crop.
    background: { color: '#00000099', padding: 16, radius: 12 },
  },
  transform: { x: 24, y, width: 1000, height: 64 },
  follows: 's1',
  dynamic: { source: 'speaker', field: 'name', participantId },
})

/**
 * Two heads: a two-person studio clip, each person's side of the frame
 * stacked in portrait with their name pinned to it, captions on a
 * translucent strip across the seam (the Translucent video skin's), the
 * seam in the theme's primary. The clip is Mixkit 2950 ("Couple of
 * broadcasters recording", Mixkit licence: free, no attribution), served
 * from Mixkit's CDN here; a copy under `static/demo` on the assets domain
 * is its durable home.
 */
export function twoHeadsDoc(): TimelineDoc {
  const duration = 11
  const SEAM = 16
  const HALF = (HEIGHT - SEAM) / 2
  // A crop clips the item's box (the Starter's rule), it does not zoom the
  // source: a box WIDTH / WINDOW wide with everything but a WINDOW-wide slice
  // around the person cropped away shows that slice filling 1080 × HALF.
  // The man sits around 30 % of the frame's width, the woman around 70 %.
  const WINDOW = 0.44
  const side = (id: string, y: number, center: number) => {
    const box = WIDTH / WINDOW
    const left = center - WINDOW / 2
    return {
      id,
      type: 'view',
      from: 0,
      duration,
      scene: 's1',
      transform: {
        x: -left * box,
        y,
        width: box,
        height: HALF,
        crop: { left, top: 0, right: 1 - left - WINDOW, bottom: 0 },
      },
      fit: 'cover',
    } satisfies TimelineItemV3
  }
  const captionsBox = { x: 80, y: 880, width: 920, height: 160 }
  // The first track draws on top.
  return doc(
    [
      {
        id: 'captions',
        items: [
          {
            id: 'captions',
            type: 'captions',
            from: 0,
            duration,
            follows: 's1',
            style: { ...captionStyle, fontSize: 52 },
            transform: captionsBox,
          },
        ],
      },
      {
        id: 'strip',
        items: [
          {
            id: 'captionsStrip',
            type: 'solid',
            from: 0,
            duration,
            color: '#000000',
            transform: {
              x: captionsBox.x - 20,
              y: captionsBox.y - 20,
              width: captionsBox.width + 40,
              height: captionsBox.height + 40,
              opacity: 0.4,
              borderRadius: 24,
            },
          },
        ],
      },
      {
        id: 'names',
        items: [
          nameChip('name-a', 'par_a', 24, duration),
          nameChip('name-b', 'par_b', HEIGHT - 24 - 64, duration),
        ],
      },
      {
        id: 'views',
        items: [side('view-a', 0, 0.3), side('view-b', HALF + SEAM, 0.7)],
      },
      {
        id: 'scenes',
        items: [
          {
            id: 's1',
            type: 'scene',
            from: 0,
            duration,
            source: SOURCE,
            sourceStart: 0,
            sourceEnd: duration,
          },
        ],
      },
      {
        id: 'background',
        items: [
          {
            id: 'background',
            type: 'solid',
            from: 0,
            duration,
            color: theme.colors.primary,
          },
        ],
      },
    ],
    {
      ass_demo_video: {
        id: 'ass_demo_video',
        type: 'video',
        filename: 'mixkit-couple-of-broadcasters-recording-2950.mp4',
        size: 0,
        remoteUrl: 'https://assets.mixkit.co/videos/2950/2950-720.mp4',
        remoteFileKey: null,
        mimeType: 'video/mp4',
        durationInSeconds: 11.094,
        hasAudioTrack: true,
        width: 1280,
        height: 720,
      },
      [`transcript:${REC}`]: {
        id: `transcript:${REC}`,
        type: 'transcript',
        recordingId: REC,
        words: DEFAULT_WORDS,
        // A (left) at 0–3.5, B (right) at 3.5–6.5, nobody identified
        // 6.5–8.8 (past the 1 s hold), then A to the end.
        speakers: turns([
          ['par_a', 0, 3.5],
          ['par_b', 3.5, 6.5],
          ['par_a', 8.8, 11],
        ]),
        participants,
        skips: [],
      },
    },
    'ass_demo_video',
  )
}

const avatarSlot = (
  id: string,
  participantId: 'par_a' | 'par_b',
  cx: number,
  duration: number,
): TimelineItemV3[] => [
  {
    id: `${id}-face`,
    type: 'component',
    componentId: 'picture',
    from: 0,
    duration,
    props: {
      source: 'speaker',
      fit: 'cover',
      shape: 'circle',
      borderColor: theme.colors.accent,
      borderWidth: 6,
    },
    transform: { x: cx - 150, y: 420, width: 300, height: 300 },
    follows: 's1',
    participantId,
  },
  {
    id: `${id}-name`,
    type: 'text',
    from: 0,
    duration,
    text: '',
    style: {
      ...textStyle,
      fontSize: 44,
      color: theme.colors.secondary,
      align: 'center',
    },
    transform: { x: cx - 210, y: 760, width: 420, height: 60 },
    follows: 's1',
    dynamic: { source: 'speaker', field: 'name', participantId },
  },
]

/**
 * Two avatars: the demo audio under two pinned avatars, each with its name,
 * the scene's visualizer and captions, and the avatar blueprint's portrait
 * foot (rule, host, title, subtitle). `par_b` has no avatar here, so the
 * placeholder disc shows.
 */
export function twoAvatarsDoc(): TimelineDoc {
  const duration = defaultClipScene.duration
  const foot: TimelineItemV3[] = [
    {
      id: 'divider',
      type: 'solid',
      from: 0,
      duration,
      color: theme.colors.secondary,
      transform: { x: 0, y: 1688, width: WIDTH, height: 2, opacity: 0.5 },
    },
    {
      id: 'host',
      type: 'component',
      componentId: 'picture',
      from: 0,
      duration,
      props: {
        source: 'url',
        url: defaultClipScene.imageUrl,
        fit: 'cover',
        shape: 'circle',
      },
      transform: { x: 50, y: 1740, width: 130, height: 130 },
    },
    {
      id: 'title',
      type: 'text',
      from: 0,
      duration,
      text: defaultClipScene.title,
      style: {
        ...textStyle,
        fontSize: 38,
        lineHeight: 1.526,
        color: theme.colors.secondary,
      },
      transform: { x: 230, y: 1743, width: 800, height: 58 },
    },
    {
      id: 'subtitle',
      type: 'text',
      from: 0,
      duration,
      text: defaultClipScene.subtitle,
      style: {
        ...textStyle,
        fontWeight: 500,
        fontSize: 38,
        lineHeight: 1.526,
        color: theme.colors.secondary,
      },
      transform: { x: 230, y: 1809, width: 580, height: 58, opacity: 0.5 },
    },
  ]
  // The first track draws on top.
  return doc(
    [
      { id: 'foot', items: foot },
      {
        id: 'captions',
        items: [
          {
            id: 'captions',
            type: 'captions',
            from: 0,
            duration,
            follows: 's1',
            style: captionStyle,
            transform: { x: 80, y: 1100, width: 920, height: 520 },
          },
        ],
      },
      {
        id: 'speakers',
        items: [
          ...avatarSlot('a', 'par_a', 300, duration),
          ...avatarSlot('b', 'par_b', 780, duration),
        ],
      },
      {
        id: 'visualizer',
        items: [
          {
            id: 'visualizer',
            type: 'component',
            componentId: 'audio-visualizer',
            from: 0,
            duration,
            props: { variant: 'bars', color: theme.colors.accent },
            transform: { x: 0, y: 860, width: WIDTH, height: 200 },
            follows: 's1',
          },
        ],
      },
      {
        id: 'scenes',
        items: [
          {
            id: 's1',
            type: 'scene',
            from: 0,
            duration,
            source: SOURCE,
            sourceStart: 0,
            sourceEnd: duration,
          },
        ],
      },
      {
        id: 'background',
        items: [
          {
            id: 'background',
            type: 'solid',
            from: 0,
            duration,
            color: theme.colors.primary,
          },
        ],
      },
    ],
    {
      ass_demo: {
        id: 'ass_demo',
        type: 'audio',
        filename: 'demo.m4a',
        size: 0,
        remoteUrl: defaultClipScene.mediaUrl,
        remoteFileKey: null,
        mimeType: 'audio/mp4',
        durationInSeconds: duration,
      },
      [`transcript:${REC}`]: {
        id: `transcript:${REC}`,
        type: 'transcript',
        recordingId: REC,
        words: DEFAULT_WORDS,
        // A, B, nobody identified 12–16, A, B.
        speakers: turns([
          ['par_a', 0.5, 7],
          ['par_b', 7, 12],
          ['par_a', 16, 22],
          ['par_b', 22, 29.5],
        ]),
        participants: participants.map((p) =>
          p.id === 'par_b' ? { ...p, avatarUrl: null } : p,
        ),
        skips: [],
      },
    },
    'ass_demo',
  )
}

/**
 * Scenes (#541): the two-heads clip as scene `s1` with its two views and
 * captions, then a blank scene `s2` (no source) holding a title card. Check
 * that the two crops stay in sync, the sound plays once, and the length
 * runs through the title card (`calculateTimelineMetadata` reads the native
 * scenes, not the lowered items).
 */
export function scenesDoc(): TimelineDoc {
  const base = twoHeadsDoc()
  const clip = 11
  const blank = 3
  const extra: TimelineItemV3[] = [
    { id: 's2', type: 'scene', from: clip, duration: blank },
    {
      id: 'title',
      type: 'component',
      componentId: 'title-card',
      from: clip,
      duration: blank,
      scene: 's2',
      props: { title: 'Thanks for watching', subtitle: 'zealous.app' },
      transform: { x: 0, y: 0, width: WIDTH, height: HEIGHT },
    },
  ]
  const items = { ...base.items }
  for (const item of extra)
    items[item.id] = hydrateItem(item, { ...size, assetId: null })
  return {
    ...base,
    items,
    tracks: [
      { id: 'title', items: ['title'], hidden: false, muted: false },
      ...base.tracks.map((t) =>
        t.id === 'scenes' ? { ...t, items: [...t.items, 's2'] } : t,
      ),
    ],
  }
}
